import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import useSupabase from '../../hooks/useSupabase';

const CategoryDropdown = ({ 
  value = '', 
  onChange, 
  placeholder = 'Select Category',
  disabled = false,
  className = '',
  showAllOption = true,
  allOptionLabel = 'All Categories'
}) => {
  const [categoriesTree, setCategoriesTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const dropdownRef = useRef(null);
  const { fetchCategoryTree } = useSupabase();

  // Load hierarchical categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        const tree = await fetchCategoryTree();
        setCategoriesTree(tree);
        setExpandedCategories(new Set());
      } catch (err) {
        console.error('Error loading category tree:', err);
        const fallbackTree = [
          { id: 'furniture', name: 'Furniture', children: [] },
          { id: 'decor', name: 'Decor', children: [] },
          { id: 'lighting', name: 'Lighting', children: [] },
          { id: 'other', name: 'Other', children: [] }
        ];
        setCategoriesTree(fallbackTree);
        setExpandedCategories(new Set());
      } finally {
        setLoading(false);
      }
    };
    loadCategories();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle category expansion
  const toggleCategory = (categoryName) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  // Handle category selection
  const selectCategory = (categoryId) => {
    onChange(categoryId);
    setIsOpen(false);
  };

  // Get display name for selected category
  const getSelectedDisplayName = () => {
    if (!value) return showAllOption ? allOptionLabel : placeholder;
    
    const findCategoryName = (categories, id) => {
      for (const category of categories) {
        if (category.id === value) return category.name;
        if (category.children && category.children.length > 0) {
          const found = findCategoryName(category.children, id);
          if (found) return found;
        }
      }
      return null;
    };
    return findCategoryName(categoriesTree, value) || 'Unknown Category';
  };

  // Render category tree recursively
  const renderCategoryTree = (categories, level = 0) => {
    return categories.map(category => {
      const hasChildren = category.children && category.children.length > 0;
      const isExpanded = expandedCategories.has(category.name);
      const isSelected = value === category.id;
      
      return (
        <div key={category.id}>
          <div 
            className={`flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer ${
              isSelected ? 'bg-blue-50 text-blue-700 font-medium' : ''
            }`}
            style={{ paddingLeft: `${level * 16 + 12}px` }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (hasChildren) toggleCategory(category.name);
              }}
              className={`p-0.5 rounded hover:bg-gray-200 ${
                !hasChildren ? 'invisible' : ''
              }`}
            >
              {hasChildren && (
                isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />
              )}
            </button>
            
            <button
              onClick={() => selectCategory(category.id)}
              className={`flex-1 text-left text-sm ${
                level === 0 ? 'font-medium' : 'font-normal'
              }`}
            >
              {category.name}
            </button>
          </div>
          
          {hasChildren && isExpanded && (
            <div>
              {renderCategoryTree(category.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className={`w-full px-3 py-2 text-left border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white flex items-center justify-between ${
          disabled || loading ? 'text-gray-400 cursor-not-allowed' : 'hover:border-gray-400'
        }`}
      >
        <span className="truncate">
          {loading ? 'Loading categories...' : getSelectedDisplayName()}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${
          isOpen ? 'rotate-180' : ''
        }`} />
      </button>
      
      {isOpen && !loading && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
          {showAllOption && (
            <div 
              className={`px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 ${
                !value ? 'bg-blue-50 text-blue-700 font-medium' : ''
              }`}
            >
              <button
                onClick={() => selectCategory('')}
                className="w-full text-left text-sm"
              >
                {allOptionLabel}
              </button>
            </div>
          )}
          
          {renderCategoryTree(categoriesTree)}
        </div>
      )}
    </div>
  );
};

export default CategoryDropdown;
